"use client";

import { IconHeart } from "@/components";
import AuthDialog from "@/components/AuthDialog/AuthDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/contexts/DialogContext";
import { wishlistService } from "@/services/api";
import { FORM_TYPES } from "@/utils";
import { useEffect, useState } from "react";

interface WishlistButtonProps {
  listingId: string;
  className?: string;
}

const WishlistButton: React.FC<WishlistButtonProps> = ({
  listingId,
  className = "",
}) => {
  const { isAuthenticated, user } = useAuth();
  const { openDialog } = useDialog();
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if item is in wishlist when component mounts
  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (!user?._id) return;
      try {
        const response = await wishlistService.isInWishlist(user._id, listingId);
        setIsInWishlist(response.inWishlist);
      } catch (error) {
        console.error("Error checking wishlist status:", error);
      }
    };

    if (isAuthenticated && user?._id) {
      checkWishlistStatus();
    }
  }, [isAuthenticated, listingId, user]);

  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      openDialog({
        component: AuthDialog,
        props: { initialMode: FORM_TYPES.LOGIN },
        options: {
          showCloseButton: true,
          closeOnOverlayClick: true,
        },
      });
      return;
    }

    if (!user?._id) return;

    setIsLoading(true);
    try {
      if (isInWishlist) {
        await wishlistService.removeFromWishlist(user._id, listingId);
        setIsInWishlist(false);
      } else {
        await wishlistService.addToWishlist(user._id, listingId);
        setIsInWishlist(true);
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleWishlistToggle}
      disabled={isLoading}
      className={`w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 ${
        isInWishlist ? "text-red" : "text-grey"
      } ${isLoading ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
    >
      <IconHeart width={16} height={16} filled={isInWishlist} />
    </button>
  );
};

export default WishlistButton;
